// client/src/pages/datasets/EditDataSet.js

import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiFetch } from "../../api/api";
import { toast } from "react-toastify";
import { useOrg } from "../orgs/org-context";
import DataSetFieldRow from "./DataSetFieldRow";

import {
  DndContext,
  closestCenter
} from "@dnd-kit/core";

import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";
import "../../styles/template.css";
import "../../styles/Buttons.css";

export default function EditDataSet() {

  const navigate = useNavigate();
  const { id } = useParams();
  const { activeOrgId } = useOrg();

  const lastOrgRef = useRef(activeOrgId);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [templateId, setTemplateId] = useState("");

  const [templates, setTemplates] = useState([]);
  const [fields, setFields] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);


  const rowStyle = useMemo(() => ({
    display: "grid",
    gridTemplateColumns: "220px minmax(0,1fr)",
    gap: 12,
    alignItems: "center"
  }), []);

  const controlStyle = useMemo(() => ({
    width: "100%",
    boxSizing: "border-box"
  }), []);


  useEffect(() => {

    if (lastOrgRef.current && activeOrgId && activeOrgId !== lastOrgRef.current) {
      navigate("/datasets");
    }

    lastOrgRef.current = activeOrgId;

  }, [activeOrgId, navigate]);


  useEffect(() => {

    (async () => {

      try {

        setLoading(true);

        const [tpls, ds] = await Promise.all([

          apiFetch("/templates/gettemplates", { method: "POST", body: { cnt: 0, lmt: 500 } }),

          apiFetch(`/datasets/get/${encodeURIComponent(id)}`, { method: "GET" })

        ]);

        const items = Array.isArray(tpls) ? tpls : (tpls?.items || []);

        setTemplates(items);

        setName(ds?.name || "");
        setDescription(ds?.description || "");
        setTemplateId(ds?.templateId || "");

        // If dataset already has field config use it
        if (ds?.fields && ds.fields.length > 0) {
          setFields(ds.fields);
        } else {

          // otherwise initialize from template
          const tpl = items.find(t => t._id === ds?.templateId);

          if (tpl?.fields) {

            const initial = tpl.fields.map((f, idx) => ({
              key: f.key,
              label: f.label || f.key,
              required: false,
              visible: true,
              order: idx + 1
            }));

            setFields(initial);

          } else {
            setFields([]);
          }

        }


      }
      catch (e) {

        toast.error("Failed to load dataset");
        navigate("/datasets");

      }
      finally {

        setLoading(false);

      }

    })();

  }, [id, navigate]);


  const updateField = (idx, prop, value) => {

    setFields(prev => {

      const copy = [...prev];
      copy[idx] = { ...copy[idx], [prop]: value };

      return copy;

    });

  };


  const handleDragEnd = (event) => {

    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = fields.findIndex(f => f.key === active.id);
    const newIndex = fields.findIndex(f => f.key === over.id);

    const reordered = arrayMove(fields, oldIndex, newIndex)
      .map((f, i) => ({ ...f, order: i + 1 }));

    setFields(reordered);

  };


  const handleSave = async () => {

    try {

      setSaving(true);
      await apiFetch(`/datasets/update/${encodeURIComponent(id)}`, {
        method: "POST",
        body: { name, description, templateId, fields }
      });

      toast.success("Data set updated.");
      navigate("/datasets");

    }
    catch (e) {

      toast.error(e?.response?.data?.message || e.message || "Update failed");

    }
    finally {

      setSaving(false);

    }

  };


  if (loading) {
    return <div style={{ padding: 16 }}>Loading…</div>;
  }


  return (

    <div className="detail-page">

      <div
        className="detail-header"
        style={{ display: "flex", justifyContent: "space-between" }}
      >

        <h2>Edit Project</h2>

        <div style={{ display: "flex", gap: 8 }}>

          <button
            className="btn-return"
            onClick={() => navigate("/datasets")}
          >
            Return
          </button>

          <button
            className="btn-green"
            disabled={saving}
            onClick={handleSave}
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>

        </div>

      </div>


      <div
        className="detail-card"
        style={{ background: "#eef2f7", padding: "16px 20px" }}
      >

        <div style={{ display: "grid", gap: 10 }}>

        <div >

          <label>Project Name</label>&nbsp;

          <input
            className="compact-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />&nbsp;&nbsp;&nbsp;&nbsp;
          </div>
          <div>

            <label>Description</label>&nbsp;
            <input
              className="compact-input"
              value={description}
              style={{ width: "80%" }}
              onChange={(e) => setDescription(e.target.value)}
            />

          </div>
          <div>
            <label><b>Data Set</b></label>&nbsp;&nbsp;&nbsp;&nbsp;
            <label>Template</label>&nbsp;
            <select
              className="compact-input"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
            >
              {templates.map((t) => (
                <option key={t._id} value={t._id}>
                  {t.name || t._id}
                </option>
              ))}
            </select>
          </div>

          <div >
          </div>




          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >

            <SortableContext
              items={fields.map(f => f.key)}
              strategy={verticalListSortingStrategy}
            >

            <div style={{ maxHeight: 320, overflowY: "auto" }}>
              <table className="compact-table">

                <thead>
                  <tr>
                    <th style={{ width: 32 }}></th>
                    <th style={{ width: 40 }}>#</th>
                    <th>Field</th>
                    <th style={{ textAlign: "center" }}>Type</th>
                    <th style={{ textAlign: "center" }}>Include</th>
                    <th style={{ textAlign: "center" }}>Visible</th>
                  </tr>
                </thead>

                <tbody style={{background: "#fff"}}>

                {fields.map((f, i) => (
                  <DataSetFieldRow
                    key={f.key}
                    field={f}
                    index={i}
                    updateField={updateField}
                  />
                ))}

                </tbody>

              </table>
              </div>
            </SortableContext>

          </DndContext>

        </div>

      </div>

    </div>

  );

}
